import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as chatSocket from '../lib/chatSocket';
import { useChatAuthToken } from './chat/useChatAuthToken';
import { Notification, NotificationsPagination } from '../types/notification';

/**
 * Server event emitted when a notification is created for the current user.
 * Payload mirrors `Notification.to_dict()` on the backend.
 */
const NOTIFICATION_CREATED_EVENT = 'notification:created';

type NotificationsCache = {
  notifications: Notification[];
  unreadCount: number;
  pagination: NotificationsPagination | undefined;
};

/**
 * Global notification socket lifecycle. Mount this once at the app root so the
 * unread badge + notifications panel update live on every page.
 *
 * It reuses the shared chat Socket.IO singleton (no second connection) — the
 * same authenticated socket carries both chat and notification events. When the
 * `notification:created` event arrives it prepends the new notification into the
 * TanStack cache for the `['notifications', params]` query family and
 * invalidates it so pagination/total counts re-sync.
 *
 * Returns early (no-op) while unauthenticated because `useChatAuthToken()`
 * resolves to null until login.
 */
export function useNotificationsSocket(): void {
  const token = useChatAuthToken();
  const qc = useQueryClient();

  useEffect(() => {
    if (!token) {
      return;
    }

    // Reuse the shared chat socket singleton. If `useChatSocket` already
    // connected it with the same token, `connect()` returns the existing
    // socket; we never open a second connection.
    const socket = chatSocket.connect({ token });

    const onNotificationCreated = (payload: Notification) => {
      // Defensive: ignore malformed payloads.
      if (!payload?.id) return;

      // Prepend into every cached notifications query (badge + panel share the
      // `['notifications', params]` family), de-duping on id.
      qc.setQueriesData<NotificationsCache>({ queryKey: ['notifications'] }, (prev) => {
        if (!prev) return prev;
        if (prev.notifications.some((n) => n.id === payload.id)) return prev;
        return {
          ...prev,
          notifications: [payload, ...prev.notifications],
        };
      });

      // Re-sync pagination/total (and any params not yet in cache).
      qc.invalidateQueries({ queryKey: ['notifications'] });
    };

    socket.on(NOTIFICATION_CREATED_EVENT, onNotificationCreated);

    return () => {
      socket.off(NOTIFICATION_CREATED_EVENT, onNotificationCreated);
    };
  }, [token, qc]);
}
